import React, { useState, useEffect, useRef } from 'react';
import { FaArrowLeft, FaChevronDown, FaArrowRight, FaTimes, FaLock } from 'react-icons/fa';
import './Deposit.css';
import winiCoin from '../../assets/images/winto-coin.png';
import { useNavigate } from "react-router-dom";
import { supabase } from '../../supabase/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from "../../contexts/LanguageContext";
import { createCheckoutSession, getStripe } from '../../utils/stripeConfig';
import FloatingSidebarButton from "../../components/FloatingSidebarButton/FloatingSidebarButton";

// flag imports
import aeflag from '../../assets/images/flags/ae_flag.png';
import frflag from '../../assets/images/flags/fr_flag.png';
import lbflag from '../../assets/images/flags/lb_flag.svg';
import qaflag from '../../assets/images/flags/qa_flag.svg';
import saflag from '../../assets/images/flags/saudi_flag.webp';
import syflag from '../../assets/images/flags/syria_flag.webp';
import usflag from '../../assets/images/flags/us_flag.png';

const flagMap = {
  AED: aeflag,
  EUR: frflag,
  LBP: lbflag,
  QAR: qaflag,
  SAR: saflag,
  SYP: syflag,
  USD: usflag
};

export default function Deposit() {
  const [winiAmount, setWiniAmount] = useState(1);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [winiToUsdRate, setWiniToUsdRate] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [cardInfo, setCardInfo] = useState({
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvc: ''
  });
  const [billingAddress, setBillingAddress] = useState({
    country: 'Lebanon'
  });
  const [savePaymentDetails, setSavePaymentDetails] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Duration popup state
  const [showDurationPopup, setShowDurationPopup] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState('6');
  const [isManualInput, setIsManualInput] = useState(false);

  const navigate = useNavigate();
  const coinRef = useRef(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const { translations: tRaw } = useLanguage();
  const t = tRaw?.depositpage || {};
  const { currentUser, userProfile } = useAuth();

  // Plan data mapping for winis amounts
  const planData = {
    'basic-6': { amount: 72, name: 'Basic', winis: 6 },
    'starter-12': { amount: 144, name: 'Starter', winis: 12 },
    'growth-30': { amount: 360, name: 'Growth', winis: 30 },
    'advanced-60': { amount: 720, name: 'Advanced', winis: 60 },
    'professional-120': { amount: 1440, name: 'Professional', winis: 120 },
    'enterprise-300': { amount: 3600, name: 'Enterprise', winis: 300 },
    'premium-600': { amount: 7200, name: 'Premium', winis: 600 },
    'ultimate-1200': { amount: 14400, name: 'Ultimate', winis: 1200 },
    'elite-3000': { amount: 36000, name: 'Elite', winis: 3000 },
    'royal-6000': { amount: 72000, name: 'Royal', winis: 6000 },
    'empire-12000': { amount: 144000, name: 'Empire', winis: 12000 },
    'legacy-30000': { amount: 360000, name: 'Legacy', winis: 30000 }
  };

  // Duration options
  const durationOptions = [
    { value: '6', label: '6 months', multiplier: 4 },
    { value: '12', label: '12 months', multiplier: 8 },
    { value: '24', label: '24 months', multiplier: 12 }
  ];

  useEffect(() => {
    // Mark wallet as verified when entering deposit page
    sessionStorage.setItem('walletVerified', 'true');
    
    const fetchData = async () => {
      try {
        // Fetch winiToUsdRate from app_settings table
        const { data: appSettings, error: settingsError } = await supabase
          .from('app_settings')
          .select('wini_to_usd_rate')
          .limit(1)
          .single();
        if (settingsError) {
          console.error('Error fetching Wini rate:', settingsError);
        } else {
          setWiniToUsdRate(appSettings?.wini_to_usd_rate || null);
        }

        // Fetch all available currencies from currencies table
        const { data: currenciesData, error: currenciesError } = await supabase
          .from('currencies')
          .select('*')
          .order('iso');

        if (currenciesError) {
          console.error('Error fetching currencies:', currenciesError);
        } else {
          const currencyList = (currenciesData || [])
            .filter(c => c.rate && c.name) // Filter out invalid entries
            .map(c => ({
              code: c.iso,
              name: c.name,
              rate: c.rate,
              USDToCoinExchangeRate: c.rate, // For compatibility
              flag: flagMap[c.iso] || usflag // fallback
            }));

          setCurrencies(currencyList);
          if (currencyList.length > 0) setSelectedCurrency(currencyList[0]);
        }
      } catch (error) {
        console.error('Error fetching deposit data:', error);
      }
    };

    fetchData();
  }, []);

  const handleClose = () => navigate(-1);

  const animateCoin = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    const coin = coinRef.current;
    coin.classList.add('coin-animate');
    setTimeout(() => {
      coin.classList.remove('coin-animate');
      setIsAnimating(false);
    }, 500);
  };

  const handleIncrement = () => {
    setWiniAmount(prev => prev + 1);
    animateCoin();
  };

  const handleDecrement = () => {
    if (winiAmount > 0) {
      setWiniAmount(prev => Math.max(0, prev - 1));
      animateCoin();
    }
  };

  const handleAmountChange = (e) => {
    const inputValue = e.target.value;
    const cursorPosition = e.target.selectionStart;
    
    if (inputValue === '' || inputValue === '0') {
      setWiniAmount(0);
      return;
    }
    const value = parseFloat(inputValue) || 0;
    setWiniAmount(Math.max(0, value));
  };

  const handleKeyDown = (e) => {
    const input = e.target;
    const currentValue = input.value;
    const cursorPosition = input.selectionStart;
    
    if (e.key >= '0' && e.key <= '9') {
      if (currentValue === '0' && cursorPosition === 1) {
        e.preventDefault();
        input.value = e.key;
        setWiniAmount(parseFloat(e.key));
        setTimeout(() => {
          input.setSelectionRange(1, 1);
        }, 0);
      }
    }
    if (e.key === 'Backspace' && currentValue === '0') {
      e.preventDefault();
      input.value = '';
      setWiniAmount(0);
    }
  };

  // --- Currency conversion helpers (numeric, no formatting) ---
  const convertUsdToSelectedNumber = (usdAmount) => {
    const n = Number(usdAmount) || 0;
    if (selectedCurrency?.rate) {
      return Number((n * selectedCurrency.rate).toFixed(2));
    }
    return Number(n.toFixed(2));
  };

  const calculateTotalNumber = () => {
    if (!selectedCurrency || !winiToUsdRate) return 0;
    const usdAmount = Number(winiAmount) * Number(winiToUsdRate);
    return convertUsdToSelectedNumber(usdAmount);
  };
  // ------------------------------------------------------------

  const calculateTotal = () => {
    const n = calculateTotalNumber();
    return n ? n.toFixed(2) : '...';
  };

  const selectCurrency = (currency) => {
    setSelectedCurrency(currency);
    setShowCurrencyDropdown(false);
  };

  // Helper functions for plan cards (formatting only)
  const formatNumberWithCommas = (value, fractionDigits = 2) => {
    const num = Number(value);
    if (!isFinite(num)) return '0';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(num);
  };

  const formatConvertedAmount = (amount, fractionDigits = 2) => {
    const n = Number(amount) || 0;
    if (selectedCurrency && selectedCurrency.rate) {
      const converted = n * selectedCurrency.rate;
      return formatNumberWithCommas(converted, fractionDigits);
    }
    return formatNumberWithCommas(n, fractionDigits);
  };

  // Handle plan selection - show duration popup for 6+ winis
  const handlePlanSelect = (planId) => {
    const plan = planData[planId];
    if (plan) {
      if (plan.winis >= 6) {
        setSelectedPlan(planId);
        setSelectedDuration('6'); // Reset to default
        setIsManualInput(false); // Plan selection
        setShowDurationPopup(true);
      } else {
        setWiniAmount(plan.winis);
        animateCoin();
      }
    }
  };

  const handleBuyWinis = async () => {
    if (!currentUser) {
      alert('Please sign in to buy winis.');
      return;
    }
    if (!selectedCurrency || !winiToUsdRate) {
      alert('Please select a currency and ensure exchange rates are loaded.');
      return;
    }

    // For 6+ winis, show duration popup instead of direct purchase
    if (winiAmount >= 6) {
      const matchingPlan = Object.keys(planData).find(planId => planData[planId].winis === winiAmount);
      if (matchingPlan) {
        setSelectedPlan(matchingPlan);
        setIsManualInput(false);
      } else {
        setSelectedPlan('custom');
        setIsManualInput(true);
      }
      setSelectedDuration('6');
      setShowDurationPopup(true);
      return;
    }

    // For < 6 winis, direct purchase
    await processDirectPurchase();
  };

  const processDirectPurchase = async () => {
    setIsProcessing(true);
    
    try {
      const stripe = await getStripe();
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      // Numeric total amount in selected currency
      const totalAmount = calculateTotalNumber();

      // Create checkout session for winis purchase (object signature)
      const { sessionId } = await createCheckoutSession({
        amount: totalAmount,
        planTitle: `${winiAmount} Winis Purchase`,
        duration: '1',          // immediate purchase
        multiplier: 1,          // no multiplier
        currency: selectedCurrency.code, // e.g. USD; stripeConfig lowercases it
        customerEmail: currentUser.email,
        metadata: {
          userId: currentUser.id,
          winisAmount: winiAmount,
          originalAmount: totalAmount,
          currency: selectedCurrency.code,
          purchaseType: 'winis',
        },
      });

      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) {
        console.error('Stripe error:', error);
        alert('Payment failed. Please try again.');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle duration selection
  const handleDurationSelect = (duration) => {
    setSelectedDuration(duration);
  };

  // Handle invest now from popup
  const handleInvestNow = async () => {
    if (isProcessing || !selectedPlan) return;

    if (!currentUser) {
      alert('Please sign in to make an investment.');
      return;
    }

    setIsProcessing(true);
    
    try {
      const stripe = await getStripe();
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      const duration = durationOptions.find(d => d.value === selectedDuration);
      if (!duration) {
        throw new Error('Invalid duration');
      }

      let planName, totalAmount, originalAmount, winisAmount;

      if (isManualInput && selectedPlan === 'custom') {
        // Manual input: convert current winis to currency now
        planName = `${winiAmount} Winis`;
        const usdAmount = Number(winiAmount) * Number(winiToUsdRate || 0);
        totalAmount = convertUsdToSelectedNumber(usdAmount);
        originalAmount = usdAmount; // original in USD
        winisAmount = winiAmount;
      } else {
        const plan = planData[selectedPlan];
        if (!plan) {
          throw new Error('Invalid plan');
        }
        planName = plan.name;
        originalAmount = plan.amount; // USD
        totalAmount = convertUsdToSelectedNumber(plan.amount); // in selected currency
        winisAmount = plan.winis;
      }

      // Create checkout session with object signature
      const { sessionId } = await createCheckoutSession({
        amount: totalAmount,
        planTitle: planName,
        duration: selectedDuration,
        multiplier: duration.multiplier,
        currency: selectedCurrency.code, // e.g. USD
        customerEmail: currentUser.email,
        metadata: {
          userId: currentUser.id,
          originalAmount,     // USD reference
          winisAmount,
          currency: selectedCurrency.code,
          purchaseType: 'investment',
          planTitle: planName,
        },
      });

      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) {
        console.error('Stripe error:', error);
        alert('Payment failed. Please try again.');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
      setShowDurationPopup(false);
    }
  };

  // Close popup
  const closeDurationPopup = () => {
    setShowDurationPopup(false);
    setSelectedPlan(null);
    setSelectedDuration('6');
    setIsManualInput(false);
  };

  const handleCloseModal = () => {
    setShowPaymentModal(false);
  };

  const handleCardInfoChange = (field, value) => {
    setCardInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePayment = () => {
    console.log('Processing payment:', {
      amount: calculateTotal(),
      currency: selectedCurrency?.code,
      cardInfo,
      billingAddress,
      savePaymentDetails
    });
    setShowPaymentModal(false);
  };

  return (
    <div className="deposit-container">
      <FloatingSidebarButton userProfile={userProfile} />
      <div className="deposit-header">
        <button className="back-button" onClick={handleClose}>
          <FaArrowLeft />
        </button>
        <h1>{t?.title || "Buy Winis"}</h1>
        <div className="currency-selector">
          <button
            className="currency-button"
            onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
          >
            {selectedCurrency && (
              <>
                <img
                  src={selectedCurrency.flag}
                  alt={selectedCurrency.code}
                  className="currency-flag-img"
                />
                {selectedCurrency.code}
              </>
            )}
            <FaChevronDown className={`dropdown-icon ${showCurrencyDropdown ? 'open' : ''}`} />
          </button>
          {showCurrencyDropdown && (
            <div className="currency-dropdown">
              {currencies.map(currency => (
                <div
                  key={currency.code}
                  className="currency-option"
                  onClick={() => selectCurrency(currency)}
                >
                  <img
                    src={currency.flag}
                    alt={currency.code}
                    className="currency-flag-img"
                  />
                  <span className="currency-text">
                    {currency.code} - {currency.name}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <p className="instruction">{t?.instruction || "Select the amount you'd like to purchase"}</p>

      <div className="conversion-row">
        <div className="currency-box">
          <span className="currency-label">{t?.winis || "Winis"}</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={winiAmount}
            onChange={handleAmountChange}
            onKeyDown={handleKeyDown}
            className="amount-input"
          />
        </div>
        <FaArrowRight className="conversion-arrow" />
        <div className="currency-box">
          <span className="currency-label">{selectedCurrency?.code}</span>
          <div className="amount-display">{calculateTotal()}</div>
        </div>
      </div>

      <div className="coin-control">
        <button className="coin-btn" onClick={handleDecrement}>-</button>
        <img ref={coinRef} src={winiCoin} alt="Wini Coin" className="coin-image" />
        <button className="coin-btn" onClick={handleIncrement}>+</button>
      </div>

      <div className="rate-info">
        <span>X {winiAmount} {t?.winis || "Winis"} = {calculateTotal()} {selectedCurrency?.code}</span>
      </div>

      {/* (plans UI commented out by you) */}

      <button 
        className="buy-button" 
        onClick={handleBuyWinis}
        disabled={isProcessing || !currentUser}
      >
        {isProcessing ? 'Processing...' : !currentUser ? 'Sign in to Buy' : (t?.buyButton || "Buy Winis")}
      </button>

      {/* Payment modal commented by you */}

      {/* Duration Selection Popup */}
      {showDurationPopup && (
        <div className="duration-popup-overlay" onClick={closeDurationPopup}>
          <div className="duration-popup" onClick={(e) => e.stopPropagation()}>
            <div className="duration-popup-header">
              <h2>Select The Duration</h2>
              <button className="duration-popup-close" onClick={closeDurationPopup}>
                ×
              </button>
            </div>
            
            <div className="duration-popup-content">
              <div className="duration-options">
                {durationOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`duration-option ${selectedDuration === option.value ? 'selected' : ''}`}
                    onClick={() => handleDurationSelect(option.value)}
                  >
                    <div className="duration-option-header">
                      <h3>{option.label}</h3>
                      <div className="duration-multiplier">×{option.multiplier}</div>
                    </div>
                    <div className="duration-option-details">
                      <p>Return: {option.multiplier * 100}%</p>
                      {selectedPlan && (
                        <p className="duration-return-amount">
                          You'll receive: {
                            isManualInput && selectedPlan === 'custom' 
                              ? formatConvertedAmount(calculateTotalNumber() * option.multiplier)
                              : formatConvertedAmount(planData[selectedPlan].amount * option.multiplier)
                          } {selectedCurrency?.code}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="duration-popup-actions">
                <button 
                  className="duration-invest-button"
                  onClick={handleInvestNow}
                  disabled={isProcessing || !currentUser}
                >
                  {isProcessing ? 'Processing...' : !currentUser ? 'Sign in to Purchase' : 'Buy Now'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
